import { useCallback, useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Upload, Loader2, AlertTriangle, Calculator } from "lucide-react";

export function RateBanner({ show }) {
  if (!show) return null;
  return <div className="mb-4 flex items-center gap-2 rounded-xl border border-sam-amber bg-sam-amber/10 p-3 text-sm font-semibold text-sam-navy" data-testid="eddm-rate-banner"><AlertTriangle className="h-4 w-4 text-sam-amber" />Verify EDDM rates at <a href="https://pe.usps.com/PriceChange" target="_blank" rel="noreferrer" className="underline">pe.usps.com/PriceChange</a></div>;
}

const FIELDS = [["retail_rate", "EDDM Retail rate ($/piece)", "0.001"], ["bmeu_rate", "EDDM BMEU rate ($/piece)", "0.001"], ["max_weight_oz", "Max weight (oz)", "0.1"],
  ["retail_cap", "Retail cap (pieces per ZIP per day)", "1"], ["print_cost_per_piece", "Print cost ($/piece)", "0.001"]];

export function PostageSettings() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/postage").then(({ data }) => setF(data)); }, []);
  if (!f) return null;
  const save = async () => {
    try { const { data } = await api.put("/admin/postage", f); setF(data); toast.success("Postage saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-4" data-testid="postage-settings">
      <RateBanner show={f.verify_banner} />
      <h3 className="font-display text-lg font-bold text-sam-navy">Postage</h3>
      <p className="text-xs text-sam-slate">USPS Notice 123 rates. Update these when USPS changes prices.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {FIELDS.map(([k, label, step]) => (
          <div key={k}><Label className="text-xs">{label}</Label><Input type="number" step={step} value={f[k] ?? ""} placeholder={k === "print_cost_per_piece" ? "Enter your printer's price" : ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`postage-${k}`} /></div>
        ))}
        <div><Label className="text-xs">Rate effective date</Label><Input type="date" value={f.rate_effective || ""} onChange={(e) => setF({ ...f, rate_effective: e.target.value })} data-testid="postage-rate_effective" /></div>
      </div>
      <Button className="mt-3 bg-sam-navy" onClick={save} data-testid="postage-save">Save postage</Button>
    </div>
  );
}

function CalcResult({ r }) {
  return (
    <div className="mt-3 grid gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-2" data-testid="calc-result">
      <div>Households: <b data-testid="calc-households">{r.households.toLocaleString()}</b></div>
      <div>Postage ({r.method === "bmeu" ? "BMEU" : "Retail"} × ${r.rate.toFixed(3)}): <b data-testid="calc-postage">${r.postage.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></div>
      <div>Card revenue{r.card_code ? ` (${r.card_code})` : " (no card yet, full-card prices)"}: <b data-testid="calc-revenue">{money(r.card_revenue)}</b></div>
      <div>Print: <b data-testid="calc-print">{r.print_cost_missing ? "not set" : `$${r.print_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</b></div>
      <div className="sm:col-span-2 text-base">Margin: <b className={r.margin >= 0 ? "text-sam-green" : "text-sam-crimson"} data-testid="calc-margin">{money(r.margin)}</b></div>
      {r.retail_warning && <div className="sm:col-span-2 rounded-md bg-sam-amber/15 p-2 font-semibold text-sam-navy" data-testid="calc-retail-warning">{r.retail_warning}</div>}
      {r.print_cost_missing && <div className="sm:col-span-2 text-xs text-sam-slate">Add a print cost per piece in Postcard Settings → Postage for a true margin.</div>}
    </div>
  );
}

function RouteTable({ rows, sel, setSel }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm" data-testid="routes-table">
        <thead><tr className="text-left text-xs uppercase text-sam-slate"><th className="p-2" /><th className="p-2">ZIP</th><th className="p-2">Route</th><th className="p-2">Type</th><th className="p-2 text-right">Residential</th><th className="p-2 text-right">Business</th><th className="p-2 text-right">Total</th><th className="p-2">Last refreshed</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.zip + r.route_id} className={`border-t border-border ${r.stale ? "bg-yellow-50" : ""}`} data-testid={`route-row-${r.zip}-${r.route_id}`}>
              <td className="p-2"><Checkbox checked={!!sel[r.route_id]} onCheckedChange={(v) => setSel({ ...sel, [r.route_id]: !!v })} data-testid={`route-check-${r.route_id}`} /></td>
              <td className="p-2">{r.zip}</td><td className="p-2 font-semibold">{r.route_id}</td><td className="p-2">{r.route_type}</td>
              <td className="p-2 text-right">{r.residential_count.toLocaleString()}</td><td className="p-2 text-right">{r.business_count.toLocaleString()}</td><td className="p-2 text-right">{r.total_count.toLocaleString()}</td>
              <td className="p-2">{r.last_refreshed?.slice(0, 10)} {r.stale && <Badge className="ml-1 bg-yellow-400 text-sam-navy" data-testid={`route-stale-${r.route_id}`}>Over 90 days old</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="p-3 text-sm text-sam-slate" data-testid="routes-empty">No routes yet. Upload the EDDM tool's export.</p>}
    </div>
  );
}

export default function RoutesAdmin() {
  const [data, setData] = useState(null);
  const [zip, setZip] = useState("");
  const [sel, setSel] = useState({});
  const [method, setMethod] = useState("retail");
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(false);
  const load = useCallback((z) => {
    api.get("/admin/routes", { params: z ? { zip: z } : {} }).then(({ data: d }) => {
      setData(d); setCalc(null);
      setSel(Object.fromEntries((d.card?.routes || []).map((r) => [r, true])));
    });
  }, []);
  useEffect(() => { load(""); api.get("/admin/postage").then(({ data: d }) => setBanner(d.verify_banner)); }, [load]);

  const upload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const rd = new FileReader();
    rd.onload = async () => {
      setBusy(true);
      try {
        const { data: d } = await api.post("/admin/routes/upload", { filename: file.name, data_b64: String(rd.result).split(",")[1] });
        toast.success(`${d.added} new, ${d.updated} updated${d.skipped ? `, ${d.skipped} rows skipped` : ""}`); load(zip);
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); e.target.value = ""; }
    };
    rd.readAsDataURL(file);
  };
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const runCalc = async () => { const { data: d } = await api.post("/admin/routes/calc", { zip, route_ids: ids, method }); setCalc(d); };
  const assign = async () => {
    try { await api.put("/admin/routes/assign", { zip, route_ids: ids }); toast.success("Routes saved to this ZIP's card — public pages now use these households"); load(zip); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save"); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin" />;

  return (
    <div className="space-y-4" data-testid="routes-admin">
      <RateBanner show={banner} />
      <div className="rounded-xl border border-border bg-white p-4">
        <h3 className="font-display text-lg font-bold text-sam-navy">EDDM routes</h3>
        <ol className="mt-1 list-decimal pl-5 text-sm text-sam-slate">
          <li>Open the USPS EDDM tool, search a ZIP, and export the route list.</li>
          <li>Upload the CSV or Excel file here. Routes are added or refreshed automatically.</li>
        </ol>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" asChild><a href="https://eddm.usps.com" target="_blank" rel="noreferrer" data-testid="open-eddm-tool"><ExternalLink className="h-4 w-4 mr-1" />Open USPS EDDM Tool</a></Button>
          <Button asChild className="bg-sam-navy"><label className="cursor-pointer" data-testid="routes-upload-label">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Upload route export<input type="file" accept=".csv,.xlsx" hidden onChange={upload} data-testid="routes-upload-input" /></label></Button>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56"><Label className="text-xs">Filter by ZIP</Label>
            <Select value={zip || "all"} onValueChange={(v) => { const z = v === "all" ? "" : v; setZip(z); load(z); }}>
              <SelectTrigger data-testid="routes-zip-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All ZIPs</SelectItem>{data.zips.map((z) => <SelectItem key={z.zip} value={z.zip}>{z.zip} · {z.routes} routes</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {zip && <span className="text-sm text-sam-slate" data-testid="routes-card-info">{data.card.has_card ? `Card ${data.card.route_code}: ${data.card.routes.length} routes picked` : "No card uses this ZIP yet"}</span>}
        </div>
        <RouteTable rows={data.routes} sel={sel} setSel={setSel} />
      </div>
      {zip && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="route-calculator">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-sam-navy"><Calculator className="h-4 w-4" />Route cost calculator</h3>
          <p className="text-xs text-sam-slate">Tick routes in the table above ({ids.length} picked), then calculate.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {["retail", "bmeu"].map((m) => <Button key={m} size="sm" variant={method === m ? "default" : "outline"} onClick={() => setMethod(m)} data-testid={`calc-method-${m}`}>{m === "retail" ? "Retail" : "BMEU"}</Button>)}
            <Button size="sm" className="bg-sam-crimson" disabled={!ids.length} onClick={runCalc} data-testid="calc-run">Calculate</Button>
            {data.card.has_card && <Button size="sm" variant="outline" onClick={assign} data-testid="routes-assign">Use these routes for this card</Button>}
          </div>
          {calc && <CalcResult r={calc} />}
        </div>
      )}
    </div>
  );
}
